/**
 * Club Settings - Club admin settings management
 * Allows club admins to manage their own club settings including age policy
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useRole } from '../../contexts/RoleContext';
import apiService from '../../services/api';
import { Button, FormInput, AdminPageLayout, Section, Card, Grid, Col } from '../../components';
import { FormActionGroup } from '../../components/admin/patterns';
import { useToast } from '../../contexts/ToastContext';

const ClubSettings = () => {
  const { user_name } = useRole();
  const navigate = useNavigate();
  const { showSuccess, showError } = useToast();
  const [clubData, setClubData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [errors, setErrors] = useState({});

  const [formData, setFormData] = useState({
    // Club Settings - Age & Team Policy
    min_age_allowed: 4,
    max_age_allowed: 20,
    default_team_capacity: 20,
  });

  useEffect(() => {
    loadClubSettings();
  }, []);

  const loadClubSettings = async () => {
    try {
      setLoading(true);
      setError(null);

      // Get current user to find club
      const userInfo = await apiService.getCurrentUser();
      const club_id = userInfo.club_id;

      if (!club_id) {
        // If no club_id found in user, try to fetch club info anyway
        // The backend middleware may have resolved the club from tenant context
        console.warn('No club_id found in user data, attempting to fetch club info from tenant context');
      }

      // Use new context-based endpoint - club from Host/X-Club-ID header
      // This will work even if club_id is not on user doc (tenant context fallback)
      let clubResponse;
      try {
        clubResponse = await apiService.get('/api/clubs/public/club-info');
      } catch (clubErr) {
        console.error('Error fetching club info:', clubErr);
        setError('Unable to access club information. Please ensure you are accessing the correct club workspace.');
        setLoading(false);
        return;
      }

      setClubData({
        id: club_id || clubResponse.id,
        name: clubResponse.name || clubResponse.display_name || 'Club Name',
        club_id: club_id || clubResponse.id
      });

      // Load club settings (use tenant-context endpoint - no club_id needed)
      try {
        const clubSettings = await apiService.getClubSettings();
        if (clubSettings) {
          setFormData({
            min_age_allowed: clubSettings.min_age_allowed || 4,
            max_age_allowed: clubSettings.max_age_allowed || 20,
            default_team_capacity: clubSettings.default_team_capacity || 20,
          });
        }
      } catch (settingsErr) {
        console.warn('Could not fetch club settings, using defaults:', settingsErr);
        // Keep default values
      }

      setLoading(false);
    } catch (err) {
      console.error('Error loading club settings:', err);
      setError(err.message || 'Failed to load club settings');
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'number' ? (value === '' ? '' : parseInt(value, 10)) : value
    }));
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Club settings validation
    const minAge = parseInt(formData.min_age_allowed);
    const maxAge = parseInt(formData.max_age_allowed);
    const capacity = parseInt(formData.default_team_capacity);

    if (isNaN(minAge) || minAge < 4 || minAge > 25) {
      newErrors.min_age_allowed = 'Minimum age must be between 4 and 25';
    }
    if (isNaN(maxAge) || maxAge < 4 || maxAge > 25) {
      newErrors.max_age_allowed = 'Maximum age must be between 4 and 25';
    }
    if (minAge >= maxAge) {
      newErrors.min_age_allowed = 'Minimum age must be less than maximum age';
    }
    if (isNaN(capacity) || capacity < 5 || capacity > 100) {
      newErrors.default_team_capacity = 'Team capacity must be between 5 and 100';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      showError('Please fix the validation errors before saving.');
      return;
    }

    if (!clubData?.id) {
      showError('Club ID not found. Please refresh the page and try again.');
      return;
    }

    try {
      setSaving(true);
      setError(null);

      // Update club settings
      const settingsData = {
        min_age_allowed: parseInt(formData.min_age_allowed),
        max_age_allowed: parseInt(formData.max_age_allowed),
        default_team_capacity: parseInt(formData.default_team_capacity)
      };

      // Use tenant-context endpoint - no club_id needed
      await apiService.updateClubSettings(null, settingsData);

      showSuccess('Club settings saved successfully!');
      setSaving(false);
    } catch (err) {
      console.error('Error saving club settings:', err);
      showError(err.message || 'Failed to save club settings');
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <AdminPageLayout
        title="CLUB SETTINGS"
        subtitle="Configure club age policy and default team settings"
        backPath="/admin/club"
        maxWidth="1400px"
      >
        <div style={{ padding: '2rem', textAlign: 'center' }}>Loading club settings...</div>
      </AdminPageLayout>
    );
  }

  if (error && !clubData) {
    return (
      <AdminPageLayout
        title="CLUB SETTINGS"
        subtitle="Configure club age policy and default team settings"
        backPath="/admin/club"
        maxWidth="1400px"
      >
        <Section title="ERROR">
          <Card level={1}>
            <div style={{ textAlign: 'center', padding: '2rem' }}>
              <h2>Error Loading Club Settings</h2>
              <p style={{ marginBottom: '1.5rem' }}>{error}</p>
              <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center' }}>
                <Button onClick={loadClubSettings} variant="save">
                  RETRY
                </Button>
                <Button onClick={() => navigate('/admin/club')} variant="outline">
                  BACK TO DASHBOARD
                </Button>
              </div>
            </div>
          </Card>
        </Section>
      </AdminPageLayout>
    );
  }

  return (
    <AdminPageLayout
      title="CLUB SETTINGS"
      subtitle="Configure club age policy and default team settings"
      backPath="/admin/club"
      maxWidth="1400px"
    >
      <form onSubmit={handleSubmit}>
        <Section title="AGE POLICY & TEAM CONFIGURATION">
          <Card level={1}>
            <p style={{ marginBottom: '2rem', color: 'var(--color-text-secondary)' }}>
              Configure club-wide age restrictions and default team settings. These apply to all programs unless overridden.
            </p>

            <Grid columns={2} gap="2rem">
              <Col>
                <FormInput
                  label="Minimum Age Allowed"
                  type="number"
                  name="min_age_allowed"
                  value={formData.min_age_allowed}
                  onChange={handleInputChange}
                  error={errors.min_age_allowed}
                  required
                  placeholder="4"
                />
              </Col>

              <Col>
                <FormInput
                  label="Maximum Age Allowed"
                  type="number"
                  name="max_age_allowed"
                  value={formData.max_age_allowed}
                  onChange={handleInputChange}
                  error={errors.max_age_allowed}
                  required
                  placeholder="20"
                />
              </Col>
            </Grid>

            <div style={{ marginTop: '1.5rem', maxWidth: '300px' }}>
              <FormInput
                label="Default Team Capacity"
                type="number"
                name="default_team_capacity"
                value={formData.default_team_capacity}
                onChange={handleInputChange}
                error={errors.default_team_capacity}
                required
                placeholder="20"
              />
            </div>
          </Card>
        </Section>

        <FormActionGroup
          onCancel={() => navigate('/admin/club')}
          saveLabel="SAVE SETTINGS"
          cancelLabel="CANCEL"
          loading={saving}
        />
      </form>
    </AdminPageLayout>
  );
};

export default ClubSettings;
